import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Box,
  Button,
  CircularProgress,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { type MouseEvent, useEffect, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import PageDataState from '@/components/PageDataState'

import {
  filterSaleReferenceRows,
  formatSaleReferenceValue,
  type SaleReferencePageConfig,
} from './saleReferenceConfig'

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none',
  fontSize: '0.7rem',
  lineHeight: 1.1,
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.text.primary, 0.14),
    '&:hover': {
      borderColor: alpha(theme.palette.text.primary, 0.24),
      backgroundColor: alpha(theme.palette.text.primary, 0.04),
    },
  },
})

interface SaleReferenceGridProps {
  page: SaleReferencePageConfig
  rows: Record<string, unknown>[]
  search: string
  onSearchChange: (value: string) => void
  loading?: boolean
  error?: unknown
  onRetry?: () => void
}

export default function SaleReferenceGrid({
  page,
  rows,
  search,
  onSearchChange,
  loading = false,
  error,
  onRetry,
}: SaleReferenceGridProps) {
  const [pageIndex, setPageIndex] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [filterPopover, setFilterPopover] = useState<{ anchor: HTMLElement } | null>(null)

  const filteredRows = useMemo(
    () =>
      filterSaleReferenceRows(rows, search, page.columns).map((row, index) => ({
        id: `${page.pageKey}-${index}`,
        ...row,
      }) as Record<string, unknown>),
    [page.pageKey, page.columns, rows, search],
  )

  useEffect(() => {
    setPageIndex(0)
  }, [page.pageKey, search])

  const visibleRows = useMemo(() => {
    if (rowsPerPage === -1) return filteredRows
    const start = pageIndex * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, pageIndex, rowsPerPage])

  const isEmpty = !loading && !error && filteredRows.length === 0
  const totalValues = useMemo(
    () =>
      new Map(
        page.columns
          .filter((column) => column.kind === 'number')
          .map((column) => {
            let total = 0
            let count = 0
            for (const row of filteredRows) {
              const value = row[column.field]
              if (typeof value !== 'number' || !Number.isFinite(value)) continue
              total += value
              count += 1
            }
            return [column.field, count > 0 ? total : null] as const
          }),
      ),
    [filteredRows, page.columns],
  )
  const minTableWidth = useMemo(
    () => Math.max(1200, page.columns.reduce((total, column) => total + (column.width ?? column.minWidth ?? 160), 0)),
    [page.columns],
  )
  const gridKey = `${page.pageKey}:${rowsPerPage}:${pageIndex}:${filteredRows.length}`
  const hasActiveFilters = Boolean(search.trim())
  const closeFilterPopover = () => setFilterPopover(null)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minHeight: 0, flex: 1 }}>
      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            minHeight: 30,
            py: 0.125,
          }}
        >
          <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontWeight: 400 }}>
              Filter
            </Typography>
            <Button
              size="small"
              variant={search.trim() ? 'contained' : 'outlined'}
              color={search.trim() ? 'primary' : 'inherit'}
              endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
              onClick={(e: MouseEvent<HTMLElement>) => setFilterPopover({ anchor: e.currentTarget })}
              sx={filterBarBtnSx}
            >
              Search
            </Button>
            {hasActiveFilters && (
              <Button
                size="small"
                variant="text"
                color="secondary"
                onClick={() => {
                  onSearchChange('')
                  setRowsPerPage(-1)
                  setPageIndex(0)
                  closeFilterPopover()
                }}
                sx={filterBarBtnSx}
              >
                Clear All
              </Button>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {loading && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Loaded: {rows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : pageIndex}
              onPageChange={(_, newPage) => setPageIndex(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPageIndex(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.72rem' },
                '& .MuiTablePagination-select': { fontSize: '0.72rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.92)', transformOrigin: 'center' },
              }}
            />
          </Stack>
        </Box>

        <Popover
          open={filterPopover !== null}
          anchorEl={filterPopover?.anchor ?? null}
          onClose={closeFilterPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { p: 1.5, minWidth: 260 } }}
        >
          <Stack spacing={1}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              label="Search pages"
              placeholder="Filter loaded rows..."
              value={search}
              onChange={(e) => {
                onSearchChange(e.target.value)
                setPageIndex(0)
              }}
            />
            <Button
              size="small"
              variant="text"
              onClick={() => {
                onSearchChange('')
                setRowsPerPage(-1)
                setPageIndex(0)
                closeFilterPopover()
              }}
            >
              Clear Search
            </Button>
          </Stack>
        </Popover>

        <PageDataState
          loading={loading}
          error={error}
          empty={isEmpty}
          emptyMessage="No rows (or empty table)."
          onRetry={onRetry}
          minHeight={300}
        >
          <Box sx={{ flex: 1, minHeight: 320, height: 620, overflow: 'hidden' }}>
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={visibleRows}
              fixedHeaderContent={() => (
                <TableRow>
                  {page.columns.map((column) => (
                    <TableCell
                      key={column.field}
                      align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                      sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}
                    >
                      {column.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {page.columns.map((column, index) => {
                    const total = column.kind === 'number' ? totalValues.get(column.field) : null
                    const text =
                      total == null ? '' : formatSaleReferenceValue(total, 'number', column.precision ?? 0)
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {index === 0 ? 'Total' : text}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: Record<string, unknown>) =>
                page.columns.map((column) => {
                  const value = formatSaleReferenceValue(row[column.field], column.kind, column.precision ?? 0)
                  return (
                    <TableCell
                      key={column.field}
                      align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                      sx={{ whiteSpace: 'nowrap', py: 0.5 }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={value || '-'}
                        sx={{ display: 'inline-block', width: '100%' }}
                      >
                        {value || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => <TableRow {...props} hover />,
              }}
            />
          </Box>
        </PageDataState>
      </Box>
    </Box>
  )
}
